'use client'

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type Context,
  type ReactNode,
  type FC,
} from 'react'
import type { Deal, Category, Stack, Comparison, Course, App } from '@/payload-types'

export type AppType = Pick<
  App,
  | 'id'
  | 'icon'
  | 'glyph'
  | 'name'
  | 'brandBackgroundColor'
  | 'brandPrimaryColor'
  | 'brandSecondaryColor'
  | 'brandTertiaryColor'
  | 'slug'
  | 'description'
  | 'homepageImage'
>

type ReviewAppType = Pick<
  App,
  | 'id'
  | 'slug'
  | 'description'
  | 'icon'
  | 'glyph'
  | 'name'
  | 'brandBackgroundColor'
  | 'brandPrimaryColor'
  | 'brandSecondaryColor'
  | 'brandTertiaryColor'
  | 'homepageImage'
>

export type NavbarData = {
  discoverMenu: {
    dealCount: number
    stackCount: number
    comparisonCount: number
    bestApps: AppType[]
    reviewsApps: ReviewAppType[]
    alternativesApps: AppType[]
  }
  dealsMenu: {
    deals: Deal[]
    categories: Category[]
    categoryCounts: Record<number, number>
  }
  stacksMenu: {
    stacks: Stack[]
    categories: Category[]
    categoryCounts: Record<number, number>
  }
  comparisonsMenu: {
    comparisons: Comparison[]
    categories: Category[]
  }
  coursesMenu: {
    courses: Course[]
  }
}

const DiscoverMenuContext = createContext<NavbarData['discoverMenu'] | null>(null)
const DealsMenuContext = createContext<NavbarData['dealsMenu'] | null>(null)
const StacksMenuContext = createContext<NavbarData['stacksMenu'] | null>(null)
const ComparisonsMenuContext = createContext<NavbarData['comparisonsMenu'] | null>(null)
const CoursesMenuContext = createContext<NavbarData['coursesMenu'] | null>(null)

function createUseContextHook<T>(
  context: Context<T | null>,
  hookDisplayName: string,
  providerDisplayName: string,
): () => T {
  return () => {
    const contextValue = useContext(context)
    if (!contextValue) {
      throw new Error(`${hookDisplayName} must be used within a ${providerDisplayName}Provider`)
    }
    return contextValue
  }
}

// generic helper to create context provider components
function createContextProvider<T>(context: Context<T | null>) {
  const Provider = ({ children, value }: { children: ReactNode; value: T }) => {
    return <context.Provider value={value}>{children}</context.Provider>
  }
  Provider.displayName = `${context.displayName || 'Context'}Provider`
  return Provider
}

const DiscoverMenuContextProvider = createContextProvider(DiscoverMenuContext)
const DealsMenuContextProvider = createContextProvider(DealsMenuContext)
const StacksMenuContextProvider = createContextProvider(StacksMenuContext)
// Used externally by ComparisonsContainer.tsx (the others are wrapped only by
// NavbarContextProvider below, so they don't need to be exported).
export const ComparisonsMenuContextProvider = createContextProvider(ComparisonsMenuContext)
const CoursesMenuContextProvider = createContextProvider(CoursesMenuContext)

export const useDiscoverMenuContext = createUseContextHook(
  DiscoverMenuContext,
  'useDiscoverMenuContext',
  'DiscoverMenuContext',
)
export const useDealsMenuContext = createUseContextHook(
  DealsMenuContext,
  'useDealsMenuContext',
  'DealsMenuContext',
)
export const useStacksMenuContext = createUseContextHook(
  StacksMenuContext,
  'useStacksMenuContext',
  'StacksMenuContext',
)
export const useComparisonsMenuContext = createUseContextHook(
  ComparisonsMenuContext,
  'useComparisonsMenuContext',
  'ComparisonsMenuContext',
)
export const useCoursesMenuContext = createUseContextHook(
  CoursesMenuContext,
  'useCoursesMenuContext',
  'CoursesMenuContext',
)

export const NavbarContextProvider: FC<{
  children: ReactNode
  initialData: NavbarData
}> = ({ children, initialData }) => {
  const [data, setData] = useState(initialData)

  useEffect(() => {
    async function refreshNavData() {
      try {
        const res = await fetch('/api/nav')
        if (!res.ok) return
        const freshData: NavbarData = await res.json()
        setData(freshData)
      } catch {
        // Silently fail — server-rendered data is still valid
      }
    }
    refreshNavData()
  }, [])

  return (
    <DiscoverMenuContextProvider value={data.discoverMenu}>
      <DealsMenuContextProvider value={data.dealsMenu}>
        <StacksMenuContextProvider value={data.stacksMenu}>
          <ComparisonsMenuContextProvider value={data.comparisonsMenu}>
            <CoursesMenuContextProvider value={data.coursesMenu}>
              {children}
            </CoursesMenuContextProvider>
          </ComparisonsMenuContextProvider>
        </StacksMenuContextProvider>
      </DealsMenuContextProvider>
    </DiscoverMenuContextProvider>
  )
}
